import * as React from 'react'
import * as ReactDOM from 'react-dom'
import FullCalendar from '@fullcalendar/react'
import dayGridPlugin from '@fullcalendar/daygrid'
import { Button } from '~/components/_atoms'
import { ChevronLeft, ChevronRight, Loader2 } from 'lucide-react'
import EventInfoDialog from './EventInfoDialog'
import { useFormatTime } from '~/hooks/useFormatTime'
import { Checkbox } from '~/lib/ui/checkbox'
import { useCoachDashboard } from '~/components/CoachDashboard/CoachDashboardProvider'
import { apiPost } from '~/util/api'
import { useTranslation } from '~/hooks/useTranslation'
import { normalizeLocale } from '~/util/normalizeLocale'

const EVENT_COLORS = {
  cancelled: '#ff4343',
  completed: '#3c3f45',
  tentative: '#ecb90d',
  requested: '#167bd7',
  confirmed: '#5caa5c',
}

const BORDER_COLORS = {
  CoachWorkshop: 'purple',
  CoachWebinar: 'orange',
  CoachRoundtable: 'teal',
}

function getLanguageTag(locale) {
  return normalizeLocale(locale)
}

function eventDisplayName(event) {
  return (
    event.extendedProps.name ||
    event.extendedProps.webinarType ||
    event.extendedProps.supportedCoachEventName ||
    event.title
  )
}

function normalizeCalendarEvent(event) {
  return {
    id: event.id,
    title: event.name || event.webinar_type || event.supported_coach_event_name,
    start: event.start_at || event.start_time,
    end: event.end_at,
    url: event.event_link || event.link,
    joinUrl: event.link,
    status: event.status,
    class: event.class,
    name: event.name,
    webinarType: event.webinar_type,
    supportedCoachEventName: event.supported_coach_event_name,
    company: event.company,
    csm: event.csm,
    coach: event.coach,
    serviceLevel: event.service_level,
    seatsRemaining: event.seats_remaining,
    invitationOrientation: event.invitation_orientation,
    networkingRoundtable: event.networking_roundtable,
    tooltipHtml: event.tooltip_html,
    isCancelled: event.is_cancelled,
    candidateDetailsUrl: event.candidate_details_url,
    isRegistered: event.is_registered,
  }
}

function calendarTheme({ isSelectCoachServicesPage, csmDashboardDisplay }) {
  if (isSelectCoachServicesPage) {
    return {
      headerBgClass: '[&_thead_.fc-scrollgrid-section-header]:bg-select-blue',
      mainColor: 'select-blue',
      mainColorHover: 'select-blue-light',
      linkColor: 'primary',
    }
  }

  if (csmDashboardDisplay) {
    return {
      headerBgClass: '[&_thead_.fc-scrollgrid-section-header]:bg-primary',
      mainColor: 'primary',
      mainColorHover: 'primary-light',
      linkColor: 'secondary',
    }
  }

  return {
    headerBgClass:
      '[&_thead_.fc-scrollgrid-section-header]:bg-intoo-green-medium-700',
    mainColor: 'intoo-green-medium-700',
    mainColorHover: 'intoo-green-medium-500',
    linkColor: 'secondary',
  }
}

function eventCardStyle(event) {
  return {
    borderColor: BORDER_COLORS[event.extendedProps.class],
    backgroundColor: EVENT_COLORS[event.extendedProps.status],
  }
}

function buildFallbackTooltip(event, formatTime) {
  const parts = []

  parts.push(`Name: ${eventDisplayName(event)}`)

  if (
    event.extendedProps.invitationOrientation &&
    event.extendedProps.serviceLevel
  ) {
    parts.push(`Program Level: ${event.extendedProps.serviceLevel}`)
  }

  if (event.start) {
    parts.push(`Date/Time: ${formatTime(event.start, 'datetime')}`)
  }

  parts.push(`Coach: ${event.extendedProps.coach || 'No Coach assigned yet'}`)

  if (event.extendedProps.company) {
    parts.push(`Company: ${event.extendedProps.company}`)
  }

  if (event.extendedProps.csm) {
    parts.push(`CSM: ${event.extendedProps.csm}`)
  }

  if (
    event.extendedProps.networkingRoundtable &&
    event.extendedProps.seatsRemaining != null
  ) {
    parts.push(`Seats Remaining: ${event.extendedProps.seatsRemaining}`)
  }

  return parts.join('\n')
}

function TooltipContent({ event, formatTime }) {
  const tooltipHtml = event.extendedProps.tooltipHtml

  if (tooltipHtml) {
    return (
      <div
        className="calendar-event-tooltip"
        dangerouslySetInnerHTML={{ __html: tooltipHtml }}
      />
    )
  }

  return (
    <div className="calendar-event-tooltip whitespace-pre-line font-roboto text-xs">
      {buildFallbackTooltip(event, formatTime)}
    </div>
  )
}

function HoverTooltip({ children, event, formatTime }) {
  const [open, setOpen] = React.useState(false)
  const [position, setPosition] = React.useState({ top: 0, left: 0 })
  const triggerRef = React.useRef(null)

  React.useEffect(() => {
    if (!open || !triggerRef.current) return
    const rect = triggerRef.current.getBoundingClientRect()
    setPosition({
      top: rect.bottom + 2,
      left: rect.left,
    })
  }, [open])

  return (
    <div
      ref={triggerRef}
      className="relative z-10"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      {children}

      {open &&
        ReactDOM.createPortal(
          <div
            className="pointer-events-none fixed z-[9999] w-80 rounded-md border border-slate-200 bg-white p-3 text-[11px] text-slate-800 shadow-xl"
            style={{ top: position.top, left: position.left }}
          >
            <TooltipContent event={event} formatTime={formatTime} />
          </div>,
          document.body,
        )}
    </div>
  )
}

function renderServiceRequestEventContent(
  eventInfo,
  formatTime,
  mainColor,
  mainColorHover,
) {
  return (
    <div className="m-2 w-full py-2">
      <div
        className={`cursor-pointer text-wrap p-1 text-left text-${mainColor} underline hover:bg-transparent hover:text-${mainColorHover}`}
      >
        {formatTime(eventInfo.event.start, 'time')} - {eventInfo.event.title}
      </div>
    </div>
  )
}

function renderEventContent(eventInfo, mainColor, mainColorHover) {
  const isCancelled = eventInfo.event.extendedProps.isCancelled
  const textColor = isCancelled ? 'text-red-700' : `text-${mainColor}`
  const hoverColor = isCancelled
    ? 'hover:text-red-500'
    : `hover:text-${mainColorHover}`
  const isRegistered = eventInfo.event.extendedProps.isRegistered
  const registeredBgColor = isRegistered ? 'bg-cod-gray-100' : ''

  return (
    <div
      className={`m-2 w-full rounded-md border border-${mainColor} px-4 py-2 ${registeredBgColor}`}
    >
      <div
        className={`cursor-pointer text-wrap p-1 text-left underline hover:bg-transparent ${textColor} ${hoverColor}`}
      >
        {eventInfo.event.title} {isRegistered && '(Registered)'}
      </div>
      {isCancelled && (
        <div className="ml-1 mt-1 text-sm text-red-700">* Event Cancelled</div>
      )}
    </div>
  )
}

function renderCoachEventContent(eventInfo, formatTime) {
  const event = eventInfo.event

  return (
    <div
      className="relative m-2 rounded-md border-2 px-4 py-2"
      style={eventCardStyle(event)}
    >
      <div className="cursor-pointer text-wrap p-1 text-left text-white underline hover:bg-transparent hover:text-white">
        <button
          className="m-0 cursor-pointer border-none bg-transparent p-0 text-white underline hover:text-white"
          type="button"
          onClick={() => {
            const url = event.extendedProps.candidateDetailsUrl || '#'
            if (url && url !== '#') {
              window.open(url, '_blank')
            }
          }}
        >
          {eventDisplayName(event)}
        </button>
      </div>

      {event.extendedProps.status !== 'cancelled' &&
        event.extendedProps.status !== 'completed' && (
          <div className="cursor-pointer text-wrap p-1 text-left text-white underline hover:bg-transparent hover:text-white">
            <button
              className="m-0 cursor-pointer border-none bg-transparent p-0 text-white underline hover:text-white"
              style={{ textDecoration: 'underline' }}
              onClick={() => window.open(event.extendedProps.joinUrl, '_blank')}
              type="button"
            >
              Join Meeting at {formatTime(event.start, 'time')}
            </button>
          </div>
        )}
    </div>
  )
}

function renderCsmEventContent(eventInfo, formatTime) {
  const event = eventInfo.event

  return (
    <HoverTooltip event={event} formatTime={formatTime}>
      <div
        className="relative m-2 rounded-md border-2 px-4 py-2"
        style={eventCardStyle(event)}
      >
        <div className="text-wrap p-1 text-left text-white underline">
          {eventDisplayName(event)}
        </div>

        {event.extendedProps.status !== 'cancelled' &&
          event.extendedProps.status !== 'completed' &&
          event.extendedProps.joinUrl && (
            <div className="text-wrap p-1 text-left">
              <button
                className="m-0 cursor-pointer border-none bg-transparent p-0 text-white underline hover:text-white"
                type="button"
                onClick={() =>
                  window.open(event.extendedProps.joinUrl, '_blank')
                }
              >
                Join Meeting at {formatTime(event.start, 'time')}
              </button>
            </div>
          )}
      </div>
    </HoverTooltip>
  )
}

function weekNumberContent(weekNumber) {
  return (
    <span className="font-roboto text-base font-medium uppercase text-white">
      {weekNumber.text}
    </span>
  )
}

function startOfWeek(date) {
  const result = new Date(date)
  const day = result.getDay()
  const diff = day === 0 ? -6 : 1 - day
  result.setDate(result.getDate() + diff)
  result.setHours(0, 0, 0, 0)
  return result
}

function addDays(date, days) {
  const result = new Date(date)
  result.setDate(result.getDate() + days)
  return result
}

function addMonths(date, months) {
  const result = new Date(date)
  result.setMonth(result.getMonth() + months)
  return result
}

function buildRange(date, view) {
  if (view === 'dayGridWeek') {
    const start = startOfWeek(date)
    const end = addDays(start, 7)
    return { start, end }
  }

  const start = new Date(date.getFullYear(), date.getMonth(), 1)
  const end = new Date(date.getFullYear(), date.getMonth() + 1, 1)
  return { start, end }
}

function toIsoString(date) {
  return date.toISOString()
}

function buildCacheKey({ targetDate, targetView, hideCancelled }) {
  const range = buildRange(targetDate, targetView)

  return JSON.stringify({
    view: targetView,
    start: toIsoString(range.start),
    end: toIsoString(range.end),
    hideCancelled,
  })
}

export function Calendar({
  calendar_events,
  eventsUrl,
  initialView = 'dayGridWeek',
  homePageDisplay,
  coachDashboardDisplay,
  csmDashboardDisplay = false,
  opRedesign = false,
  coachServices = false,
  disableDialog = false,
  isSelectCoachServicesPage = false,
  locale,
}) {
  const { hideCancelledMeetings, setHideCancelledMeetings } =
    useCoachDashboard() || {}
  const hideCancelled = coachDashboardDisplay ? hideCancelledMeetings : false
  const setHideCancelled = coachDashboardDisplay
    ? setHideCancelledMeetings
    : () => {}

  const usesRemoteEvents = calendar_events == null && !!eventsUrl

  const initialCalendarView =
    opRedesign && !homePageDisplay ? 'dayGridMonth' : initialView

  const initialEvents = React.useMemo(
    () => (calendar_events || []).map(normalizeCalendarEvent),
    [calendar_events],
  )

  const [view, setView] = React.useState(initialCalendarView)
  const [calendarDate, setCalendarDate] = React.useState(new Date())
  const [calendarMonthYear, setCalendarMonthYear] = React.useState('')
  const [dialogEvent, setDialogEvent] = React.useState(null)
  const [showEventInfoDialog, setShowEventInfoDialog] = React.useState(false)
  const [events, setEvents] = React.useState(initialEvents)
  const [isLoading, setIsLoading] = React.useState(usesRemoteEvents)

  const formatTime = useFormatTime()
  const { t } = useTranslation('Calendar')
  const calendarRef = React.useRef(null)
  const requestIdRef = React.useRef(0)
  const eventsCacheRef = React.useRef(new Map())

  const { headerBgClass, mainColor, mainColorHover, linkColor } = React.useMemo(
    () =>
      calendarTheme({
        isSelectCoachServicesPage,
        csmDashboardDisplay,
      }),
    [isSelectCoachServicesPage, csmDashboardDisplay],
  )

  React.useEffect(() => {
    if (!usesRemoteEvents) {
      setEvents(initialEvents)
      setIsLoading(false)
    }
  }, [usesRemoteEvents, initialEvents])

  React.useEffect(() => {
    if (!(calendarDate instanceof Date) || isNaN(calendarDate)) return

    setCalendarMonthYear(
      calendarDate
        .toLocaleString(getLanguageTag(locale), {
          month: 'long',
          year: 'numeric',
        })
        .replace(/^./, (c) => c.toUpperCase()),
    )
  }, [calendarDate, locale])

  const visibleEvents = React.useMemo(() => {
    if (coachDashboardDisplay && hideCancelled && !usesRemoteEvents) {
      return events.filter(
        (event) => !event.status || event.status !== 'cancelled',
      )
    }

    return events
  }, [events, coachDashboardDisplay, hideCancelled, usesRemoteEvents])

  const loadEventsFor = React.useCallback(
    async ({ targetDate, targetView }) => {
      if (!usesRemoteEvents) return true

      const cacheKey = buildCacheKey({
        targetDate,
        targetView,
        hideCancelled,
      })

      const cachedEvents = eventsCacheRef.current.get(cacheKey)

      if (cachedEvents) {
        setEvents(cachedEvents)
        setCalendarDate(targetDate)
        setView(targetView)

        const api = calendarRef.current?.getApi()
        if (api) {
          api.changeView(targetView, targetDate)
          api.setOption('locale', getLanguageTag(locale))
        }

        return true
      }

      const requestId = requestIdRef.current + 1
      requestIdRef.current = requestId
      setIsLoading(true)

      try {
        const range = buildRange(targetDate, targetView)
        const params = new URLSearchParams({
          start: toIsoString(range.start),
          end: toIsoString(range.end),
        })

        if (coachDashboardDisplay && hideCancelled) {
          params.append('hide_cancelled', 'true')
        }

        const response = await fetch(`${eventsUrl}?${params.toString()}`, {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
        })

        if (!response.ok) {
          throw new Error(`Failed to load calendar events (${response.status})`)
        }

        const data = await response.json()

        if (requestId !== requestIdRef.current) return false

        const normalizedEvents = (data || []).map(normalizeCalendarEvent)
        eventsCacheRef.current.set(cacheKey, normalizedEvents)

        setEvents(normalizedEvents)
        setCalendarDate(targetDate)
        setView(targetView)

        const api = calendarRef.current?.getApi()
        if (api) {
          api.changeView(targetView, targetDate)
          api.setOption('locale', getLanguageTag(locale))
        }

        return true
      } catch (error) {
        console.error('Failed to load calendar events', error)
        return false
      } finally {
        if (requestId === requestIdRef.current) {
          setIsLoading(false)
        }
      }
    },
    [usesRemoteEvents, eventsUrl, coachDashboardDisplay, hideCancelled, locale],
  )

  React.useEffect(() => {
    if (!usesRemoteEvents) return

    loadEventsFor({
      targetDate: new Date(),
      targetView: initialCalendarView,
    })
  }, [usesRemoteEvents, loadEventsFor, initialCalendarView])

  const handleHideCancelledChange = async (checked) => {
    try {
      await apiPost('/coach/toggle_hide_cancelled_meetings', {
        hide_cancelled_meetings: checked,
      })

      setHideCancelled(checked)

      if (usesRemoteEvents) {
        await loadEventsFor({
          targetDate: calendarDate,
          targetView: view,
        })
      }
    } catch (error) {
      console.error('Failed to update hide_cancelled_meetings', error)
    }
  }

  async function goNext() {
    if (!calendarRef.current || isLoading) return

    if (!usesRemoteEvents) {
      const api = calendarRef.current.getApi()
      api.next()
      setCalendarDate(api.getDate())
      return
    }

    const targetDate =
      view === 'dayGridMonth'
        ? addMonths(calendarDate, 1)
        : addDays(calendarDate, 7)

    await loadEventsFor({ targetDate, targetView: view })
  }

  async function goPrev() {
    if (!calendarRef.current || isLoading) return

    if (!usesRemoteEvents) {
      const api = calendarRef.current.getApi()
      api.prev()
      setCalendarDate(api.getDate())
      return
    }

    const targetDate =
      view === 'dayGridMonth'
        ? addMonths(calendarDate, -1)
        : addDays(calendarDate, -7)

    await loadEventsFor({ targetDate, targetView: view })
  }

  async function goToday() {
    if (!calendarRef.current || isLoading) return

    if (!usesRemoteEvents) {
      const api = calendarRef.current.getApi()
      api.today()
      setCalendarDate(api.getDate())
      return
    }

    await loadEventsFor({
      targetDate: new Date(),
      targetView: view,
    })
  }

  async function toggleView() {
    if (!calendarRef.current || isLoading) return

    const targetView = view === 'dayGridMonth' ? 'dayGridWeek' : 'dayGridMonth'

    if (!usesRemoteEvents) {
      const api = calendarRef.current.getApi()
      setView(targetView)
      api.changeView(targetView)
      setCalendarDate(api.getDate())
      return
    }

    await loadEventsFor({
      targetDate: calendarDate,
      targetView,
    })
  }

  function handleEventClick(eventInfo) {
    setDialogEvent(eventInfo.event)
    setShowEventInfoDialog(true)
  }

  function toggleShowEventInfoDialog() {
    setShowEventInfoDialog((prev) => !prev)
  }

  function renderCalendarEventContent(eventInfo) {
    if (coachDashboardDisplay) {
      return renderCoachEventContent(eventInfo, formatTime)
    }

    if (csmDashboardDisplay) {
      return renderCsmEventContent(eventInfo, formatTime)
    }

    if (coachServices) {
      return renderServiceRequestEventContent(
        eventInfo,
        formatTime,
        mainColor,
        mainColorHover,
      )
    }

    return renderEventContent(eventInfo, mainColor, mainColorHover)
  }

  const changeViewText =
    view === 'dayGridMonth' ? t('view_this_week') : t('view_full_schedule')
  const aspectRatio = view === 'dayGridMonth' ? 1.3 : 5.4

  return (
    <>
      <style
        dangerouslySetInnerHTML={{
          __html: `
            .fc-css-vars {
              --fc-today-bg-color: #F0F0F0;
              --fc-border-color: #B1B1B1;
              --fc-more-link-bg-color: #fff;
            }
            .fc-col-header-cell-cushion {
              width: 100%;
              text-align: left;
              padding: 0.75rem !important;
            }
            .fc-col-header {
              border-radius: 8px !important;
            }
            .fc-scrollgrid-section > td {
              border-radius: 0px 0px 8px 8px !important;
            }
            .fc-scrollgrid-section > th {
              border-radius: 0px 16x 16px 0px !important;
            }
            .fc-scrollgrid {
              border-radius: 8px !important;
              background: #FFF;
            }
            .fc-daygrid-dot-event:hover {
              background-color: unset;
            }
            .calendar-event-tooltip {
              font-size: 11px;
              line-height: 1.35;
              max-width: 20rem;
              white-space: normal;
            }
            .calendar-event-tooltip p {
              margin: 0 0 0.25rem 0;
            }
            .calendar-event-tooltip p:last-child {
              margin-bottom: 0;
            }
            .fc .fc-daygrid-event-harness {
              z-index: 1;
            }
            .fc .fc-daygrid-event-harness:hover {
              z-index: 9999;
            }
          `,
        }}
      />

      {((homePageDisplay && !coachServices) || isSelectCoachServicesPage) && (
        <div className="lg:absolute lg:bottom-[calc(100%+1rem)] lg:right-0">
          <Button
            variant="link"
            color={linkColor}
            onClick={toggleView}
            className="px-0 py-0"
            disabled={usesRemoteEvents && isLoading}
          >
            {changeViewText}
          </Button>
        </div>
      )}

      <div className={`fc-css-vars ${headerBgClass}`}>
        <div className="mb-2 flex items-end justify-between">
          {!homePageDisplay && !opRedesign && (
            <div className="ml-[-2rem] flex w-48">
              <Button
                variant="link"
                color={linkColor}
                onClick={toggleView}
                disabled={usesRemoteEvents && isLoading}
              >
                {changeViewText}
              </Button>
            </div>
          )}

          {view === 'dayGridMonth' && (
            <div className="mb-2 flex h-9 -space-x-px">
              <Button
                onClick={goPrev}
                variant="outline"
                color={linkColor}
                size="sm"
                className="rounded-r-none focus:z-10"
                disabled={usesRemoteEvents && isLoading}
              >
                <ChevronLeft />
              </Button>
              <Button
                onClick={goToday}
                variant="outline"
                color={linkColor}
                size="sm"
                className="rounded-none focus:z-10"
                disabled={usesRemoteEvents && isLoading}
              >
                {t('today')}
              </Button>
              <Button
                onClick={goNext}
                variant="outline"
                color={linkColor}
                size="sm"
                className="rounded-l-none focus:z-10"
                disabled={usesRemoteEvents && isLoading}
              >
                <ChevronRight />
              </Button>

              {coachDashboardDisplay && (
                <div className="!ml-8 flex items-center">
                  <Checkbox
                    checked={hideCancelled}
                    onCheckedChange={handleHideCancelledChange}
                    id="hide-cancelled"
                    disabled={usesRemoteEvents && isLoading}
                  />
                  <label
                    htmlFor="hide-cancelled"
                    className="!mb-0 ml-2 cursor-pointer text-intoo-green-coachcta"
                  >
                    {t('hide_cancelled_meetings')}
                  </label>
                </div>
              )}
            </div>
          )}

          {view === 'dayGridMonth' && (
            <div className="mb-2 flex w-48 items-center justify-end gap-2 text-base">
              {usesRemoteEvents && isLoading && (
                <Loader2 className="h-4 w-4 animate-spin" />
              )}
              {calendarMonthYear}
            </div>
          )}
        </div>

        <div className="relative">
          {usesRemoteEvents && isLoading && (
            <div className="absolute inset-0 z-20 flex items-center justify-center rounded-md bg-white/70 backdrop-blur-[1px]">
              <div className="flex items-center gap-2 rounded-full border bg-white px-4 py-2 shadow-sm">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span className="text-sm">Loading events...</span>
              </div>
            </div>
          )}

          <FullCalendar
            ref={calendarRef}
            plugins={[dayGridPlugin]}
            initialView={view}
            initialDate={calendarDate}
            aspectRatio={aspectRatio}
            contentHeight="auto"
            weekends={false}
            events={visibleEvents}
            eventContent={renderCalendarEventContent}
            fixedWeekCount={false}
            dayHeaderContent={weekNumberContent}
            headerToolbar={false}
            eventClick={
              coachDashboardDisplay && !disableDialog
                ? handleEventClick
                : undefined
            }
            locale={getLanguageTag(locale)}
          />
        </div>
      </div>

      <EventInfoDialog
        dialogEvent={dialogEvent}
        showEventInfoDialog={showEventInfoDialog}
        toggleShowEventInfoDialog={toggleShowEventInfoDialog}
      />
    </>
  )
}
